import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://builtfuturelife.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  // Login endpoint
  login: (usernameOrEmail, password) => 
    api.post('/signin', { usernameOrEmail, password }),
  
  // Register endpoint
  register: (userData) => 
    api.post('/signup', userData),
  
  // Logout function
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
  },
  
  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
  
  // Get current user role
  getCurrentRole: () => {
    return localStorage.getItem('role');
  }
};

export const userAPI = {
  // Get user profile
  getProfile: () => api.get('/profile'),
  
  // Update profile
  updateProfile: (data) => api.put('/profile/update', data),
  
  // Change password
  changePassword: (data) => api.put('/profile/change-password', data),
  
  // Get user balance
  getBalance: () => api.get('/balance'),
  
  // Get transaction history
  getHistory: () => api.get('/history'),
  
  // Get referrals
  getReferrals: () => api.get('/profile/referrals'),
  
  // Bank Details Management
  getBankDetails: () => api.get('/profile/bank-details'),
  saveBankDetails: (data) => api.post('/profile/bank-details', data),
  
  // Wallet Management
  getWallets: () => api.get('/profile/wallets'),
  addWallet: (walletData) => api.post('/profile/wallets', walletData),
  updateWallet: (walletId, walletData) => api.put(`/profile/wallets/${walletId}`, walletData),
  deleteWallet: (walletId) => api.delete(`/profile/wallets/${walletId}`),
  setDefaultWallet: (walletId) => api.put(`/profile/wallets/${walletId}/default`),
  
  // Settings Management
  getSettings: () => api.get('/profile/settings'),
  saveNotificationSettings: (settings) => api.put('/profile/settings/notifications', settings),
  saveAppearanceSettings: (settings) => api.put('/profile/settings/appearance', settings),
  
  // 2FA Management
  enable2FA: (code) => api.post('/profile/2fa/enable', { code }),
  disable2FA: () => api.post('/profile/2fa/disable'),
  get2FAQR: () => api.get('/profile/2fa/qr'),
  
  // Account Management
  requestAccountDeletion: () => api.post('/profile/delete-request'),
  exportData: () => api.get('/profile/export-data'),
  
  // KYC Verification
  submitKYC: (formData) => api.post('/profile/kyc', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  getKYCStatus: () => api.get('/profile/kyc/status'),
  
  // Security Settings
  getLoginHistory: () => api.get('/profile/security/login-history'),
  getActiveSessions: () => api.get('/profile/security/sessions'),
  terminateSession: (sessionId) => api.delete(`/profile/security/sessions/${sessionId}`),
  terminateAllSessions: () => api.delete('/profile/security/sessions/all'),
};


export const investmentAPI = {
  // Get all investment plans
  getPlans: () => api.get('/plans'),
  
  // Buy a plan
  buyPlan: (planId) => api.post('/buy-plan', { planId }),
  
  // Get active investments
  getActiveInvestments: () => api.get('/investments/active'),
  
  // Get investment history
  getInvestmentHistory: () => api.get('/investments/history'),
  
  // Calculate investment returns
  calculateReturns: (data) => api.post('/investments/calculate', data),
  
  // Get investment statistics
  getInvestmentStats: () => api.get('/investments/stats'),
  
  // Withdraw from investment (early withdrawal if allowed)
  withdrawInvestment: (investmentId) => api.post(`/investments/${investmentId}/withdraw`),
};

export const transactionAPI = {
  // Deposit funds
  deposit: (data) => api.post('/deposit', data),
  
  // Withdraw funds
  withdraw: (data) => api.post('/withdraw', data),
  
  // Get deposit methods
   getDepositMethods: () => api.get('/deposit/methods'),
  
  // Get withdrawal methods
  getWithdrawalMethods: () => api.get('/transactions/withdrawal-methods'),
  
  // Get transaction by ID
  getTransaction: (transactionId) => api.get(`/transactions/${transactionId}`),
  
  // Cancel pending transaction
  cancelTransaction: (transactionId) => api.delete(`/transactions/${transactionId}`),
  
  // Get transaction limits
  getLimits: () => api.get('/transactions/limits'),
  
  // Get exchange rates (if you have crypto)
  getExchangeRates: () => api.get('/transactions/exchange-rates'),
};

export const referralAPI = {
  // Get referral statistics
  getReferralStats: () => api.get('/referrals/stats'),
  
  // Get referral earnings
  getReferralEarnings: () => api.get('/referrals/earnings'),
  
  // Get referral history
  getReferralHistory: () => api.get('/referrals/history'),
  
  // Generate referral link
  generateReferralLink: () => api.post('/referrals/generate-link'),
  
  // Get referral banners/materials
  getReferralMaterials: () => api.get('/referrals/materials'),
};

export const supportAPI = {
  // Create support ticket
  createTicket: (data) => api.post('/support/tickets', data),
  
  // Get all tickets
  getTickets: () => api.get('/support/tickets'),
  
  // Get single ticket
  getTicket: (ticketId) => api.get(`/support/tickets/${ticketId}`),
  
  // Reply to ticket
  replyToTicket: (ticketId, data) => api.post(`/support/tickets/${ticketId}/reply`, data),
  
  // Close ticket
  closeTicket: (ticketId) => api.put(`/support/tickets/${ticketId}/close`),
  
  // Get FAQs
  getFAQs: () => api.get('/support/faqs'),
  
  // Get support categories
  getCategories: () => api.get('/support/categories'),
};

export const notificationAPI = {
  // Get all notifications
  getNotifications: () => api.get('/notifications'),
  
  // Mark notification as read
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  
  // Mark all as read
  markAllAsRead: () => api.put('/notifications/read-all'),
  
  // Delete notification
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
  
  // Delete all notifications
  deleteAllNotifications: () => api.delete('/notifications/all'),
  
  // Get notification settings
  getNotificationSettings: () => api.get('/notifications/settings'),
  
  // Update notification settings
  updateNotificationSettings: (settings) => api.put('/notifications/settings', settings),
};

export const adminAPI = {
  // Admin endpoints (only accessible by admin)
  getUsers: () => api.get('/admin/users'),
  getUser: (userId) => api.get(`/admin/user/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/user/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/user/${userId}`),
  
  // Transactions
  getPendingDeposits: () => api.get('/admin/deposits/pending'),
  getPendingWithdrawals: () => api.get('/admin/withdrawals/pending'),
  approveTransaction: (type, id) => api.post('/admin/approve', { type, id }),
  rejectTransaction: (type, id, reason) => api.post('/admin/reject', { type, id, reason }),
  getAllTransactions: (params) => api.get('/admin/transactions', { params }),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  getDashboardStats: () => api.get('/admin/dashboard-stats'),
  
  // Investment Plans Management
  createPlan: (planData) => api.post('/admin/plans', planData),
  updatePlan: (planId, planData) => api.put(`/admin/plans/${planId}`, planData),
  deletePlan: (planId) => api.delete(`/admin/plans/${planId}`),
  
  // Support (admin side)
  getAdminTickets: () => api.get('/admin/support/tickets'),
  replyAsAdmin: (ticketId, data) => api.post(`/admin/support/tickets/${ticketId}/reply`, data),
  
  // System Settings
  getSystemSettings: () => api.get('/admin/settings'),
  updateSystemSettings: (settings) => api.put('/admin/settings', settings),
  
  // Maintenance
  getSystemLogs: () => api.get('/admin/system/logs'),
  clearCache: () => api.post('/admin/system/clear-cache'),
  backupDatabase: () => api.post('/admin/system/backup'),
};

// Utility function to handle file uploads
export const uploadFile = async (file, endpoint) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return api.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Utility function to handle file downloads
export const downloadFile = async (endpoint, filename) => {
  const response = await api.get(endpoint, {
    responseType: 'blob'
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// WebSocket setup for real-time updates (if needed)
export const setupWebSocket = (userId) => {
  const wsUrl = API_URL.replace('https', 'wss') + `/ws?userId=${userId}&token=${localStorage.getItem('token')}`;
  const socket = new WebSocket(wsUrl);
  
  socket.onopen = () => {
    console.log('WebSocket connected');
  };
  
  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    // Handle real-time notifications
    if (data.type === 'notification') {
      // Dispatch notification event
      window.dispatchEvent(new CustomEvent('new-notification', { detail: data.data }));
    }
    // Handle balance updates
    if (data.type === 'balance-update') {
      window.dispatchEvent(new CustomEvent('balance-updated', { detail: data.data }));
    }
    // Handle deposit/withdrawal status updates
    if (data.type === 'transaction-update') {
      window.dispatchEvent(new CustomEvent('transaction-updated', { detail: data.data }));
    }
  };
  
  socket.onclose = () => {
    console.log('WebSocket disconnected');
    // Try to reconnect after 5 seconds
    setTimeout(() => setupWebSocket(userId), 5000);
  };
  
  return socket;
};

// Helper function to format API errors
export const formatError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return {
      message: error.response.data.message || 'An error occurred',
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // The request was made but no response was received
    return {
      message: 'No response from server. Please check your internet connection.',
      status: 0
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      message: error.message || 'An unexpected error occurred',
      status: -1
    };
  }
};

// Helper function to set auth token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  }
};

// Helper function to get auth token
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper function to set user data
export const setUserData = (userData) => {
  localStorage.setItem('user', JSON.stringify(userData));
};

// Helper function to get user data
export const getUserData = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// Helper function to clear all auth data
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('user');
  delete api.defaults.headers.common['Authorization'];
};

export default api;